/** What the signed-in user may do — for deciding what to SHOW. RIGHTS_MODEL.md §6.1.
 *
 * FKMT, 11 September 2026: *"Let the browser know each user's rights, so
 * screens only show buttons the user can actually use."*
 *
 * A SIGN ON THE DOOR, NOT THE LOCK. Every endpoint still checks every request
 * (RIGHTS_MODEL.md §9). This list only decides which buttons appear, so a
 * person is not offered an action that will refuse. Tampering with it in the
 * browser gains nothing: the button appears and the server says no.
 *
 * HIDDEN UNTIL KNOWN. `can()` answers false until the list has arrived, so a
 * button never appears and then vanishes.
 *
 * WHEN IT REFRESHES — FKMT chose option A:
 *   - when the company or year changes (the provider is keyed on them)
 *   - on every change of screen          (App calls `refresh`)
 *   - when the tab regains focus
 *   - straight after any refusal (403)   (api.ts calls the handler below)
 *   - NEVER on a timer — UI_TASTE.md: no constant polling
 */

import { createContext, useCallback, useContext, useEffect, useMemo,
         useRef, useState, type ReactNode } from 'react'
import { api, setForbiddenHandler } from './api'
import { isMockMode } from './mock'

export interface Rights {
  /** The list has arrived. Until then every `can` is false. */
  known: boolean
  /** May the user do this? `module.object.action`, exactly as the server checks. */
  can: (permission: string) => boolean
  /** Holds at least one permission ending in `.action` — e.g. any `.approve`. */
  canAny: (suffix: string) => boolean
  /** Ask again. Cheap, and safe to call as often as a screen changes. */
  refresh: () => void
}

const NOTHING: Rights = {
  known: false, can: () => false, canAny: () => false, refresh: () => {},
}
const RightsContext = createContext<Rights>(NOTHING)

export function RightsProvider({ children }: { children: ReactNode }) {
  const [perms, setPerms] = useState<Set<string> | null>(null)
  const inFlight = useRef(false)

  const refresh = useCallback(() => {
    // One request at a time: a screen change and a focus event arriving
    // together should not become two identical requests.
    if (inFlight.current) return
    inFlight.current = true
    api.get<{ permissions: string[] }>('/admin/me/permissions')
      .then(r => setPerms(new Set(r.permissions ?? [])))
      // Keep the last good list rather than blanking every button on a
      // network blip. The server still decides each request.
      .catch(() => {})
      .finally(() => { inFlight.current = false })
  }, [])

  useEffect(() => {
    if (isMockMode()) return
    refresh()
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    setForbiddenHandler(refresh)
    return () => {
      window.removeEventListener('focus', onFocus)
      setForbiddenHandler(() => {})
    }
  }, [refresh])

  const value = useMemo<Rights>(() => isMockMode() ? {
    // No backend, no rights to read: sample data shows every action, under
    // the banner that says it is sample data.
    known: true, can: () => true, canAny: () => true, refresh,
  } : ({
    known: perms !== null,
    can: p => perms?.has(p) ?? false,
    canAny: suffix => perms !== null && [...perms].some(p => p.endsWith(suffix)),
    refresh,
  }), [perms, refresh])

  return <RightsContext.Provider value={value}>{children}</RightsContext.Provider>
}

export function useRights(): Rights {
  return useContext(RightsContext)
}

/** The rights for one object, named the way the server names them —
 *  `useObjectRights('masters', 'bom').can('release')`. */
export function useObjectRights(module: string, object: string) {
  const r = useRights()
  return useMemo(() => ({
    known: r.known,
    can: (action: string) => r.can(`${module}.${object}.${action}`),
  }), [r, module, object])
}

/** Fixed rights, for tests: render a screen as a given person would see it.
 *  `perms="all"` holds everything. Never used by the application itself. */
export function StaticRights(
  { perms, children }: { perms: string[] | 'all'; children: ReactNode },
) {
  const set = perms === 'all' ? null : new Set(perms)
  const value: Rights = {
    known: true,
    can: p => set === null || set.has(p),
    canAny: suffix => set === null || [...set].some(p => p.endsWith(suffix)),
    refresh: () => {},
  }
  return <RightsContext.Provider value={value}>{children}</RightsContext.Provider>
}
